import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Package, Users, Truck, History, FileBarChart2,
  AlertTriangle, LogOut, Menu, X, Wrench, Users2, HardHat,
  MapPinned, ChevronDown, ScrollText, Archive, ClipboardList, CheckCircle2, Calendar, Palmtree, FileText,
  ScanLine, Star, LayoutGrid, ChevronLeft, ChevronRight, ShieldCheck, PhoneCall, Boxes,
  ArrowUpFromLine, BriefcaseMedical, BookOpen, CalendarClock, FileSignature, Gauge,
  FileCheck2, Satellite, GraduationCap, Send,
} from "lucide-react";
import api from "@/lib/api";
import QRScanner from "@/components/QRScanner";
import CercaGlobale from "@/components/CercaGlobale";
import MenuProfilo from "@/components/MenuProfilo";
import SceltaQR from "@/components/SceltaQR";
import Raccoglitore from "@/components/Raccoglitore";
import { photoUrl } from "@/lib/placeholders";

// Nove categorie, e dentro ognuna quello che le appartiene.
//
// `short` e' l'etichetta usata nella striscia dei preferiti, dove lo spazio
// conta: il nome per esteso resta nel menu completo. L'ordine e'
// quello con cui si lavora: prima le persone, poi quello che si da' loro, poi
// dove vanno, poi con cosa, e in fondo le carte.
//
// Il menu era cresciuto una voce alla volta e in cima ne erano rimaste sette
// sciolte, senza un criterio: chi cercava la localizzazione dei mezzi non
// pensava di trovarla fuori da "Mezzi e attrezzature".
//
// Dove una categoria ha una pagina sola resta un collegamento diretto: farne
// un gruppo da aprire per trovarci dentro una voce sola aggiungerebbe un
// tocco senza dare niente in cambio.
const MENU = [
  { to: "/", label: "Dashboard", short: "Dashboard", icon: LayoutDashboard, roles: ["admin", "amministrativo", "operator", "tecnico", "preposto"] },
  // Subito sotto la Dashboard, fuori da ogni gruppo: la segnalazione si
  // fa nel momento in cui si vede qualcosa che non va, e la fa chiunque.
  // Dentro una tendina qualcuna si perderebbe per strada.
  { to: "/segnalazioni", label: "Segnalazioni", short: "Segnalazioni", icon: ClipboardList, roles: ["admin", "amministrativo", "tecnico", "preposto", "operator"], badge: true },
  {
    group: "Dipendenti", icon: Users, roles: ["admin", "amministrativo", "tecnico"], items: [
      { to: "/dipendenti", label: "Elenco e fascicoli", short: "Dipendenti", icon: Users, roles: ["admin", "amministrativo", "tecnico"] },
      { to: "/scadenzario", label: "Scadenzario", short: "Scadenzario", icon: CalendarClock, roles: ["admin", "amministrativo", "tecnico"] },
      { to: "/discrepanze", label: "Discrepanze documenti", short: "Discrepanze", icon: AlertTriangle, roles: ["admin"] },
      { to: "/ferie-permessi", label: "Ferie e permessi", short: "Ferie", icon: Palmtree, roles: ["admin", "amministrativo", "tecnico"] },
      { to: "/reperibilita", label: "Reperibilità", short: "Reperibilità", icon: PhoneCall, roles: ["admin", "amministrativo", "tecnico"] },
      { to: "/nomine-sicurezza", label: "Nomine e incarichi sicurezza", short: "Nomine", icon: ShieldCheck, roles: ["admin", "amministrativo", "tecnico"] },
      { to: "/archivio-dipendenti", label: "Archivio ex dipendenti", short: "Ex dipendenti", icon: Archive, roles: ["admin", "amministrativo"] },
    ],
  },
  {
    group: "DPI", icon: Package, roles: ["admin", "amministrativo", "operator", "tecnico"], items: [
      { to: "/consegna", label: "Nuova consegna", short: "Consegna", icon: Truck, roles: ["admin", "amministrativo", "operator", "tecnico"] },
      { to: "/storico", label: "Storico consegne", short: "Storico", icon: History, roles: ["admin", "amministrativo", "operator", "tecnico"] },
      { to: "/magazzino", label: "Magazzino DPI", short: "Magazzino", icon: Package, roles: ["admin", "amministrativo", "operator", "tecnico"] },
    ],
  },
  {
    group: "Cantieri", icon: HardHat, roles: ["admin", "amministrativo", "tecnico", "preposto"], items: [
      { to: "/cantieri", label: "Gestione cantieri", short: "Cantieri", icon: HardHat, roles: ["admin", "amministrativo", "tecnico", "preposto"] },
      { to: "/area-operativa", label: "Area operativa (mappa comuni)", short: "Area", icon: MapPinned, roles: ["admin", "amministrativo", "tecnico", "preposto"] },
      { to: "/pianificazione-griglia", label: "Pianificazione", short: "Pianificazione", icon: Calendar, roles: ["admin", "amministrativo", "tecnico"] },
      { to: "/verifiche-cantiere", label: "Verifiche in cantiere", short: "Verifiche", icon: CheckCircle2, roles: ["admin", "amministrativo", "tecnico", "preposto"] },
      { to: "/reportistica-sicurezza", label: "Reportistica sicurezza", short: "Sicurezza", icon: FileBarChart2, roles: ["admin", "amministrativo", "tecnico"] },
    ],
  },
  {
    group: "Mezzi e attrezzature", icon: Wrench, roles: ["admin", "amministrativo", "operator", "tecnico", "preposto"], items: [
      { to: "/attrezzature", label: "Elenco", short: "Mezzi", icon: Wrench, roles: ["admin", "amministrativo", "operator", "tecnico"] },
      { to: "/localizzazione", label: "Localizzazione mezzi", short: "Localizzazione", icon: MapPinned, roles: ["admin", "amministrativo", "tecnico"] },
      { to: "/giornata-mezzi", label: "Giornata dei mezzi", short: "Giornata", icon: Satellite, roles: ["admin"] },
      { to: "/costi-manutenzione", label: "Costi manutenzione", short: "Costi", icon: FileBarChart2, roles: ["admin", "amministrativo", "tecnico"] },
      { to: "/tachigrafo", label: "Tachigrafo", short: "Tachigrafo", icon: Gauge, roles: ["admin", "amministrativo", "tecnico"] },
      { to: "/cassette", label: "Cassette di primo soccorso", short: "Cassette", icon: BriefcaseMedical, roles: ["admin", "amministrativo", "tecnico", "preposto"] },
      { to: "/archivio-attrezzature", label: "Archivio dismesse", short: "Dismesse", icon: Archive, roles: ["admin", "amministrativo"] },
    ],
  },
  {
    // Il gruppo si chiama "Materiali" e non "Magazzino materiali" perche' una
    // delle due voci dentro porta gia' quel nome: ripeterlo sopra e sotto si
    // legge male.
    group: "Materiali", icon: Boxes, roles: ["admin", "amministrativo", "operator", "tecnico", "preposto"], items: [
      { to: "/movimenti-materiali", label: "Carichi e prelievi", short: "Prelievi", icon: ArrowUpFromLine, roles: ["admin", "amministrativo", "operator", "tecnico", "preposto"] },
      { to: "/magazzino-materiali", label: "Magazzino materiali", short: "Materiali", icon: Boxes, roles: ["admin", "amministrativo", "operator", "tecnico", "preposto"] },
    ],
  },
  // La formazione erogata in casa sta per conto suo, come i sistemi di
  // gestione: e' un mestiere a parte, con il suo catalogo, le sue aule e i
  // suoi registri, e dentro "Dipendenti" spariva.
  { to: "/formatore", label: "Formatore", short: "Formatore", icon: GraduationCap, roles: ["admin"] },
  { to: "/sistemi-integrati", label: "Sistemi integrati", short: "Sistemi", icon: ShieldCheck, roles: ["admin", "amministrativo", "tecnico"] },
  // Il registro infortuni sta accanto ai sistemi di gestione e non dentro:
  // e' la voce che si apre di corsa quando succede qualcosa, e cercarla in
  // fondo a una sezione con dentro altre venti schede non andava bene.
  { to: "/infortuni", label: "Infortuni e incidenti", short: "Infortuni", icon: AlertTriangle, roles: ["admin", "amministrativo", "tecnico"] },
  {
    group: "Documenti aziendali", icon: FileText, roles: ["admin", "amministrativo", "tecnico", "preposto"], items: [
      { to: "/documenti-aziendali", label: "Elenco documenti", short: "Doc. aziendali", icon: FileText, roles: ["admin", "amministrativo", "tecnico", "preposto"] },
      { to: "/ddt", label: "Documenti di trasporto", short: "DDT", icon: Truck, roles: ["admin", "amministrativo", "tecnico"] },
      // Le lettere ufficiali che escono dall'azienda. Anche l'ufficio
      // tecnico: le trasmissioni ai committenti nascono da li'.
      { to: "/protocollo", label: "Protocollo in uscita", short: "Protocollo", icon: Send, roles: ["admin", "amministrativo", "tecnico"] },
    ],
  },
  // Queste due restano fuori dai gruppi come la segnalazione: i contatori
  // sono il lavoro che il magazzino ha per le mani adesso e ci si torna
  // cinquanta volte al giorno, e il manuale serve proprio a chi non sa dove
  // cercare, quindi non si nasconde dietro una tendina.
  //
  // Lavoro a termine: quando la partita di contatori e' finita, questa voce
  // si toglie e con lei la pagina.
  { to: "/contatori", label: "Contatori Italgas", short: "Contatori", icon: Gauge, roles: ["admin", "amministrativo", "operator", "tecnico"] },
  // Il manuale se lo scarica chiunque, ognuno trova in cima il suo.
  { to: "/manuali", label: "Manuali d'uso", short: "Manuali", icon: BookOpen, roles: ["admin", "amministrativo", "tecnico", "preposto", "operator"] },
  {
    group: "Admin", icon: ScrollText, roles: ["admin"], items: [
      { to: "/preventivi", label: "Preventivi", short: "Preventivi", icon: FileSignature, roles: ["admin"] },
      { to: "/pos-appalto", label: "POS di appalto", short: "POS appalto", icon: FileCheck2, roles: ["admin"] },
      { to: "/reportistica", label: "Reportistica", short: "Reportistica", icon: FileBarChart2, roles: ["admin"] },
      { to: "/utenti", label: "Utenti", short: "Utenti", icon: Users2, roles: ["admin"] },
      { to: "/registro", label: "Registro attività", short: "Registro", icon: ScrollText, roles: ["admin"] },
    ],
  },
];

// Nessun preferito proposto: se li sceglie ognuno con la stellina. Prima ne
// venivano proposti cinque pensati sull'ufficio, e chi lavora al magazzino o in
// cantiere si trovava in barra pagine che non apre mai.
const DEFAULT_FAVORITES = [];

function filterForRole(role) {
  return MENU
    .filter(entry => entry.roles.includes(role))
    .map(entry => entry.items
      ? { ...entry, items: entry.items.filter(i => i.roles.includes(role)) }
      : entry)
    .filter(entry => !entry.items || entry.items.length > 0);
}

/** Tutte le pagine raggiungibili dal ruolo, senza raggruppamenti. */
function flattenForRole(role) {
  const out = [];
  for (const entry of filterForRole(role)) {
    if (entry.items) out.push(...entry.items.map(i => ({ ...i, group: entry.group })));
    else out.push({ ...entry, group: null });
  }
  return out;
}

// Un preferito puo' essere una singola pagina oppure un gruppo intero: nel
// secondo caso in barra compare un pulsante che apre le sue sottovoci.
const chiaveGruppo = (nome) => `gruppo:${nome}`;
const eGruppo = (chiave) => String(chiave).startsWith("gruppo:");
const nomeDelGruppo = (chiave) => String(chiave).slice(7);

function initialFavorites(pages) {
  const available = new Set(pages.map(p => p.to));
  return DEFAULT_FAVORITES.filter(p => available.has(p));
}

function UserAvatar({ user, className = "w-9 h-9" }) {
  const src = photoUrl(user?.photo_url);
  const initials = (user?.name || "?")
    .split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase();
  if (src) {
    return <img src={src} alt="" title={user?.name}
                className={`${className} rounded object-cover border border-slate-200 flex-shrink-0`} />;
  }
  return (
    <div title={user?.name}
         className={`${className} rounded bg-[#0067b2] text-white flex items-center justify-center text-xs font-bold flex-shrink-0`}>
      {initials}
    </div>
  );
}

/** Menu completo: da qui si naviga ovunque e si scelgono i preferiti.
 *
 * Compatto: si vedono le nove categorie e basta. Le sottovoci escono di lato
 * passandoci sopra col mouse, e restano finché non si va su un'altra.
 *
 * Prima erano tutte aperte insieme su due colonne: con quaranta pagine era
 * un muro di righe in cui bisognava cercare, e la categoria non serviva a
 * niente perché tanto si vedeva tutto lo stesso.
 *
 * Il tocco funziona come il passaggio del mouse — sul tablet il mouse non
 * c'è — e sul telefono questo pannello non si apre nemmeno: lì c'è il menu a
 * tendina, che per il pollice è più comodo.
 */
function FullMenu({ entries, favorites, onToggleFavorite, openReports }) {
  const [open, setOpen] = useState(false);
  const [gruppoAperto, setGruppoAperto] = useState(null);
  const ref = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Chiudendo il menu si dimentica anche quale categoria era aperta:
  // riaprendolo si riparte pulito invece di ritrovarsi una tendina di lato.
  useEffect(() => { if (!open) setGruppoAperto(null); }, [open]);

  const row = (it) => {
    const Icon = it.icon;
    const fav = favorites.includes(it.to);
    const active = it.to === "/" ? location.pathname === "/" : location.pathname.startsWith(it.to);
    return (
      <div key={it.to} className={`flex items-center gap-1 rounded ${active ? "bg-orange-50" : "hover:bg-slate-50"}`}>
        <NavLink to={it.to} onClick={() => setOpen(false)}
                 className={`flex-1 min-w-0 px-2 py-2 text-sm flex items-center gap-2 ${active ? "text-orange-700 font-medium" : "text-slate-700"}`}
                 data-testid={`nav-${it.to.replace("/", "") || "dashboard"}`}>
          <Icon className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">{it.label}</span>
          {it.badge && openReports > 0 && (
            <span className="bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0">
              {openReports}
            </span>
          )}
        </NavLink>
        <button type="button" onClick={() => onToggleFavorite(it.to)}
                title={fav ? "Togli dai preferiti" : "Aggiungi ai preferiti"}
                data-testid={`fav-toggle-${it.to.replace("/", "") || "dashboard"}`}
                className="p-1.5 mr-1 rounded hover:bg-white flex-shrink-0">
          <Star className={`w-4 h-4 ${fav ? "fill-orange-400 text-orange-400" : "text-slate-300"}`} />
        </button>
      </div>
    );
  };

  return (
    // Sul telefono il menu sta nella barra in basso: questo pulsante non si
    // mostra, altrimenti i menu sono due.
    <div className="relative flex-shrink-0 hidden md:block" ref={ref}>
      <button type="button" onClick={() => setOpen(o => !o)} data-testid="full-menu-btn"
              className="px-3 py-2 rounded text-sm font-medium flex items-center gap-1.5 text-slate-700 hover:bg-slate-100 transition-colors">
        <LayoutGrid className="w-4 h-4" />
        <span className="hidden sm:inline">Menu</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl p-2 z-50 w-64">
          <p className="text-[11px] text-slate-500 mb-1.5 px-1.5">
            Passa sopra una categoria per vedere cosa contiene. La stellina
            tiene una voce — o un gruppo intero — sempre in barra.
          </p>
          {entries.map(entry => entry.items ? (
            <div key={entry.group} className="relative"
                 onMouseEnter={() => setGruppoAperto(entry.group)}>
              <div className={`flex items-center gap-1 rounded cursor-default ${
                gruppoAperto === entry.group ? "bg-slate-100" : "hover:bg-slate-50"}`}>
                <button type="button" data-testid={`gruppo-${entry.group}`}
                        onClick={() => setGruppoAperto(g => g === entry.group ? null : entry.group)}
                        className="flex-1 min-w-0 px-2 py-2 text-sm flex items-center gap-2 text-slate-700 text-left">
                  {entry.icon && <entry.icon className="w-4 h-4 flex-shrink-0" />}
                  <span className="flex-1 truncate">{entry.group}</span>
                  <span className="text-[10px] text-slate-400 tabular-nums">{entry.items.length}</span>
                  <ChevronRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                </button>
                <button type="button" onClick={() => onToggleFavorite(chiaveGruppo(entry.group))}
                        title={favorites.includes(chiaveGruppo(entry.group))
                          ? "Togli il gruppo dalla barra" : "Tieni tutto il gruppo in barra"}
                        data-testid={`fav-gruppo-${entry.group}`}
                        className="p-1.5 mr-1 rounded hover:bg-white flex-shrink-0">
                  <Star className={`w-4 h-4 ${favorites.includes(chiaveGruppo(entry.group))
                    ? "fill-orange-400 text-orange-400" : "text-slate-300"}`} />
                </button>
              </div>

              {/* Le sottovoci escono di lato, non sotto: aprendosi sotto
                  spingerebbero giù tutte le altre categorie e il menu
                  tornerebbe lungo come prima. */}
              {gruppoAperto === entry.group && (
                <div className="absolute left-full top-0 ml-1 w-64 bg-white border border-slate-200
                                rounded-lg shadow-xl p-1 z-50 max-h-[60vh] overflow-y-auto"
                     data-testid={`sottomenu-${entry.group}`}>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 px-2 py-1">
                    {entry.group}
                  </div>
                  {entry.items.map(it => row(it))}
                </div>
              )}
            </div>
          ) : (
            <div key={entry.to} onMouseEnter={() => setGruppoAperto(null)}>
              {row(entry)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Preferito che è un gruppo: un pulsante che apre le sue sottovoci.
 *
 * Il pannello vive in un portale ancorato al pulsante e non dentro la
 * striscia: la striscia scorre in orizzontale, e un contenitore che ritaglia
 * su un asse ritaglia anche sull'altro, per cui la tendina si apriva ma
 * restava tagliata sotto il bordo.
 */
function GruppoInBarra({ entry, openReports }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const bottone = useRef(null);
  const pannello = useRef(null);
  const location = useLocation();
  const attivo = entry.items.some(i => location.pathname.startsWith(i.to));

  const LARGHEZZA = 240;
  const colloca = useCallback(() => {
    const r = bottone.current?.getBoundingClientRect();
    if (!r) return;
    // Vicino al bordo destro si allinea a destra, altrimenti uscirebbe fuori.
    const sinistra = Math.min(r.left, window.innerWidth - LARGHEZZA - 8);
    setPos({ top: r.bottom + 4, left: Math.max(8, sinistra) });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const fuori = (e) => {
      if (bottone.current?.contains(e.target)) return;
      if (pannello.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", fuori);
    window.addEventListener("resize", colloca);
    // Se la striscia scorre il pulsante si sposta: la tendina lo insegue
    // invece di chiudersi, altrimenti basterebbe un rientro automatico del
    // pulsante nell'area visibile per farla sparire appena aperta.
    window.addEventListener("scroll", colloca, true);
    return () => {
      document.removeEventListener("mousedown", fuori);
      window.removeEventListener("resize", colloca);
      window.removeEventListener("scroll", colloca, true);
    };
  }, [open, colloca]);

  const Icona = entry.icon;
  return (
    <>
      <button type="button" ref={bottone}
              onClick={() => { if (open) { setOpen(false); } else { colloca(); setOpen(true); } }}
              data-testid={`fav-gruppo-btn-${entry.group}`}
              className={`px-2.5 py-2 rounded text-sm font-medium flex items-center gap-1.5 whitespace-nowrap flex-shrink-0 transition-colors ${
                attivo ? "bg-orange-500 text-white" : "text-slate-700 hover:bg-slate-100"}`}>
        {Icona && <Icona className="w-4 h-4" />}{entry.group}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && pos && createPortal(
        <div ref={pannello} data-testid={`fav-gruppo-tenda-${entry.group}`}
             style={{ position: "fixed", top: pos.top, left: pos.left, width: LARGHEZZA }}
             className="bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-[60]">
          {entry.items.map(it => {
            const Icon = it.icon;
            return (
              <NavLink key={it.to} to={it.to} onClick={() => setOpen(false)}
                       className={({ isActive }) =>
                         `px-4 py-2.5 text-sm flex items-center gap-2 transition-colors ${
                           isActive ? "bg-orange-50 text-orange-700 font-medium" : "text-slate-700 hover:bg-slate-50"}`}>
                <Icon className="w-4 h-4" />{it.label}
                {it.badge && openReports > 0 && (
                  <span className="bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                    {openReports}
                  </span>
                )}
              </NavLink>
            );
          })}
        </div>, document.body)}
    </>
  );
}

/** I preferiti nell'ordine in cui sono stati scelti, mescolando gruppi e pagine. */
function preferitiOrdinati(pages, entries, favorites) {
  return favorites.map(chiave => eGruppo(chiave)
    ? (() => {
        const e = entries.find(x => x.items && x.group === nomeDelGruppo(chiave));
        return e ? { tipo: "gruppo", chiave, entry: e } : null;
      })()
    : (() => {
        const p = pages.find(x => x.to === chiave);
        return p ? { tipo: "pagina", chiave, page: p } : null;
      })()).filter(Boolean);
}

/** Gli stessi preferiti ma tutti come pagine: un gruppo diventa le sue voci.
 *
 * Sulla barra in basso non c'e' spazio per una tendina che si apre: il pollice
 * deve trovare la pagina, non un altro menu.
 */
function paginePreferite(pages, entries, favorites) {
  const fuori = [];
  for (const v of preferitiOrdinati(pages, entries, favorites)) {
    if (v.tipo === "pagina") fuori.push(v.page);
    else fuori.push(...v.entry.items);
  }
  return fuori;
}

/** Striscia orizzontale dei preferiti, con frecce quando non ci stanno tutti. */
function FavoritesStrip({ pages, entries, favorites, openReports }) {
  const scroller = useRef(null);
  const [overflow, setOverflow] = useState(false);

  const check = useCallback(() => {
    const el = scroller.current;
    if (el) setOverflow(el.scrollWidth > el.clientWidth + 4);
  }, []);

  useEffect(() => {
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [check, favorites]);

  const scroll = (dir) => scroller.current?.scrollBy({ left: dir * 200, behavior: "smooth" });
  const ordered = preferitiOrdinati(pages, entries, favorites);

  if (ordered.length === 0) {
    return <div className="flex-1 min-w-0 text-xs text-slate-400 px-2 hidden md:block">
      Nessun preferito: scegli le voci dal menu con la stellina.
    </div>;
  }

  return (
    <div className="flex-1 min-w-0 hidden md:flex items-center gap-1">
      {overflow && (
        <button type="button" onClick={() => scroll(-1)} className="p-1 text-slate-400 hover:text-slate-700 flex-shrink-0">
          <ChevronLeft className="w-4 h-4" />
        </button>
      )}
      <div ref={scroller} onScroll={check}
           className="flex items-center gap-1 overflow-x-auto no-scrollbar flex-1 min-w-0">
        {ordered.map(v => {
          if (v.tipo === "gruppo") {
            return <GruppoInBarra key={v.chiave} entry={v.entry} openReports={openReports} />;
          }
          const p = v.page;
          const Icon = p.icon;
          return (
            <NavLink key={p.to} to={p.to} end={p.to === "/"}
                     className={({ isActive }) =>
                       `px-2.5 py-2 rounded text-sm font-medium flex items-center gap-1.5 whitespace-nowrap flex-shrink-0 transition-colors ${
                         isActive ? "bg-orange-500 text-white" : "text-slate-700 hover:bg-slate-100"
                       }`}
                     data-testid={`fav-${p.to.replace("/", "") || "dashboard"}`}>
              <Icon className="w-4 h-4" />{p.short || p.label}
              {p.badge && openReports > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {openReports}
                </span>
              )}
            </NavLink>
          );
        })}
      </div>
      {overflow && (
        <button type="button" onClick={() => scroll(1)} className="p-1 text-slate-400 hover:text-slate-700 flex-shrink-0">
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

/**
 * La barra in basso, solo sul telefono: dove arriva il pollice.
 *
 * In cantiere l'app si usa con una mano sola, e la striscia dei preferiti sta
 * in alto e sul telefono non compare nemmeno: per aprire qualunque cosa
 * servivano tre tocchi (menu, gruppo, voce). Qui invece Menu e QR sono sempre
 * a portata, e in mezzo ci sono le pagine che quella persona si e' messa da
 * parte con la stellina.
 *
 * I pulsanti stanno staccati dal bordo di quasi due centimetri e non e' un
 * capriccio: il fondo dello schermo su iPhone appartiene alla barra di sistema,
 * e una scorsa orizzontale li' cambia applicazione invece di far scorrere le
 * icone nascoste. Nemmeno ci si affida al solo env(safe-area-inset-bottom), che
 * vale zero finche' la pagina non dichiara viewport-fit=cover - e dichiararlo
 * cambierebbe l'impaginazione di tutta l'app, non solo di questa barra.
 */
function BarraInBasso({ pages, entries, favorites, openReports, menuAperto,
                        onMenu, onScan }) {
  const location = useLocation();
  const preferite = paginePreferite(pages, entries, favorites);

  const cella = "flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 " +
                "rounded min-w-[58px] flex-shrink-0 text-[10px] font-medium leading-tight";

  return (
    <nav data-testid="barra-basso"
         className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur
                    border-t border-slate-200 flex items-stretch gap-1 px-1 pt-1
                    pb-[max(1.75rem,env(safe-area-inset-bottom))]">
      <button type="button" onClick={onMenu} data-testid="barra-basso-menu"
              className={`${cella} ${menuAperto ? "bg-orange-500 text-white" : "text-slate-600"}`}>
        {menuAperto ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        <span>Menu</span>
      </button>

      <div className="flex-1 min-w-0 flex items-stretch gap-1 overflow-x-auto no-scrollbar">
        {preferite.length === 0 ? (
          <div className="flex items-center text-[10px] text-slate-400 px-2 leading-tight">
            Tocca la stellina nel menu per tenere qui le pagine che usi.
          </div>
        ) : preferite.map(p => {
          const Icon = p.icon;
          const attiva = p.to === "/" ? location.pathname === "/"
                                      : location.pathname.startsWith(p.to);
          return (
            <NavLink key={p.to} to={p.to} end={p.to === "/"}
                     data-testid={`barra-basso-${p.to.replace("/", "") || "dashboard"}`}
                     className={`${cella} relative ${
                       attiva ? "bg-orange-500 text-white" : "text-slate-600"}`}>
              <Icon className="w-5 h-5" />
              <span className="truncate max-w-[58px]">{p.short || p.label}</span>
              {p.badge && openReports > 0 && (
                <span className="absolute top-0.5 right-1 bg-red-500 text-white text-[9px]
                                 font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {openReports}
                </span>
              )}
            </NavLink>
          );
        })}
      </div>

      <button type="button" onClick={onScan} data-testid="barra-basso-qr"
              className={`${cella} bg-orange-500 text-white`}>
        <ScanLine className="w-5 h-5" />
        <span>QR</span>
      </button>
    </nav>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileGroup, setMobileGroup] = useState(null);
  const [openReports, setOpenReports] = useState(0);
  const [showScanner, setShowScanner] = useState(false);
  const [sceltaQR, setSceltaQR] = useState(null);

  // Il pallino lo vede chi le segnalazioni le riceve: ufficio tecnico,
  // amministrazione e direzione. Prima l'amministrativo era escluso e le
  // vedeva solo aprendo la pagina di proposito.
  useEffect(() => {
    if (!["admin", "amministrativo", "tecnico"].includes(user?.role)) return;
    const load = () => api.get("/reports/open-count").then(({ data }) => setOpenReports(data.count)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user?.role]);

  const entries = useMemo(() => filterForRole(user?.role), [user?.role]);
  const pages = useMemo(() => flattenForRole(user?.role), [user?.role]);

  // I preferiti restano sul dispositivo, distinti per utente: su un tablet
  // condiviso ognuno ritrova i suoi.
  const storageKey = `sgm_nav_fav_${user?.email || "anon"}`;
  const [favorites, setFavorites] = useState([]);
  useEffect(() => {
    if (!user?.role) return;
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(storageKey) || "null"); } catch { saved = null; }
    const available = new Set(pages.map(p => p.to));
    const gruppi = new Set(entries.filter(e => e.items).map(e => chiaveGruppo(e.group)));
    setFavorites(Array.isArray(saved)
      ? saved.filter(v => available.has(v) || gruppi.has(v))
      : initialFavorites(pages));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, user?.role, pages.length]);

  const toggleFavorite = (to) => {
    setFavorites(prev => {
      let next;
      if (prev.includes(to)) {
        next = prev.filter(p => p !== to);
      } else if (eGruppo(to)) {
        // Il gruppo copre gia' le sue voci: tenerle anche singole raddoppierebbe.
        const dentro = new Set((entries.find(e => e.items && e.group === nomeDelGruppo(to))?.items || [])
          .map(i => i.to));
        next = [...prev.filter(p => !dentro.has(p)), to];
      } else {
        next = [...prev, to];
      }
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* spazio esaurito: pazienza */ }
      return next;
    });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-2">
          {/* Il logo riporta alla Dashboard. Serve a tutti, ma soprattutto
              all'amministrazione, che la Dashboard non ce l'ha nel menu e una
              volta uscita non aveva piu' modo di tornarci. */}
          <NavLink to="/" end title="Torna alla Dashboard" className="flex-shrink-0">
            <img src="/assets/sgm-logo.svg" alt="SGM Impianti" className="h-12 sm:h-14 w-auto" />
          </NavLink>

          <FullMenu entries={entries} favorites={favorites}
                    onToggleFavorite={toggleFavorite} openReports={openReports} />

          <FavoritesStrip pages={pages} entries={entries} favorites={favorites} openReports={openReports} />

          <div className="flex items-center gap-2 flex-shrink-0 ml-auto md:ml-0">
            {/* Il cerca: casella sul computer, lente sul telefono. */}
            {user?.role !== "visitatore" && <CercaGlobale />}
            <MenuProfilo user={user} avatar={<UserAvatar user={user} />} />
            {/* Sul telefono lo scanner sta nella barra in basso, dove arriva il
                pollice: qui in alto resta solo per chi lavora al computer. */}
            {user?.role !== "visitatore" && (
              <Button variant="outline" size="sm" className="hidden md:inline-flex"
                      onClick={() => setShowScanner(true)}
                      title="Scansiona un QR" data-testid="qr-scan-btn">
                <ScanLine className="w-4 h-4" />
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => { logout(); nav("/login"); }} data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white px-4 py-2 flex flex-col max-h-[75vh] overflow-y-auto">
            <div className="flex items-center gap-2 py-2 sm:hidden">
              <UserAvatar user={user} className="w-8 h-8" />
              <div className="leading-tight">
                <div className="text-sm font-semibold">{user?.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{user?.role}</div>
              </div>
            </div>
            {entries.map(entry =>
              entry.items ? (
                <div key={entry.group}>
                  <button type="button" onClick={() => setMobileGroup(g => g === entry.group ? null : entry.group)}
                          className="w-full px-3 py-3 text-sm font-semibold text-slate-500 flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      {entry.icon && <entry.icon className="w-4 h-4" />}
                      {entry.group}
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileGroup === entry.group ? "rotate-180" : ""}`} />
                  </button>
                  {mobileGroup === entry.group && entry.items.map(it => {
                    const Icon = it.icon;
                    return (
                      <div key={it.to} className="flex items-center">
                        <NavLink to={it.to} onClick={() => { setMobileOpen(false); setMobileGroup(null); }}
                                 className={({ isActive }) => `flex-1 pl-6 px-3 py-2.5 text-sm flex items-center gap-2 ${isActive ? "text-orange-600 font-medium" : "text-slate-700"}`}>
                          <Icon className="w-4 h-4" />{it.label}
                        </NavLink>
                        <button type="button" onClick={() => toggleFavorite(it.to)} className="p-2">
                          <Star className={`w-4 h-4 ${favorites.includes(it.to) ? "fill-orange-400 text-orange-400" : "text-slate-300"}`} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (() => {
                  const Icon = entry.icon;
                  return (
                    <div key={entry.to} className="flex items-center">
                      <NavLink to={entry.to} end={entry.to === "/"} onClick={() => setMobileOpen(false)}
                               className={({ isActive }) => `flex-1 px-3 py-3 rounded text-sm font-medium flex items-center gap-3 ${isActive ? "bg-orange-500 text-white" : "text-slate-700"}`}>
                        <Icon className="w-4 h-4" />{entry.label}
                      </NavLink>
                      <button type="button" onClick={() => toggleFavorite(entry.to)} className="p-2">
                        <Star className={`w-4 h-4 ${favorites.includes(entry.to) ? "fill-orange-400 text-orange-400" : "text-slate-300"}`} />
                      </button>
                    </div>
                  );
                })()
            )}
          </div>
        )}
      </header>
      {/* Il fondo lascia posto alla barra in basso, che sul telefono galleggia
          sopra il contenuto. */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-6 pb-32 md:pb-6">
        <Outlet />
      </main>
      <footer className="border-t border-slate-200 py-4 pb-28 md:pb-4 text-center text-xs text-slate-500">
        SGM Impianti srl · SGM APP v1.1
      </footer>

      <BarraInBasso pages={pages} entries={entries} favorites={favorites}
                    openReports={openReports} menuAperto={mobileOpen}
                    onMenu={() => setMobileOpen(o => !o)}
                    onScan={() => setShowScanner(true)} />

      {showScanner && (
        <QRScanner
          onClose={() => setShowScanner(false)}
          onScanned={(target) => {
            setShowScanner(false);
            setMobileOpen(false);
            // Non si salta piu' alla pagina: si chiede cosa fare.
            setSceltaQR(target);
          }} />
      )}
      {sceltaQR && <SceltaQR target={sceltaQR} user={user} onChiudi={() => setSceltaQR(null)} />}

      {/* Il raccoglitore: la linguetta sul bordo destro, sempre a portata
          mentre si gira per l'app. */}
      <Raccoglitore />
    </div>
  );
}
